import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { EventTypes, PublicEventsService, ValidationResult } from 'angular-auth-oidc-client'
import { Subscription, filter } from 'rxjs'
import { SharedService } from '../shared/services/shared.service'

/**
 * Redirect URI target — deliberately PASSIVE. The root component's single
 * checkAuth() call consumes the code/state from this URL; on success the
 * library itself navigates to postLoginRoute ('/').
 *
 * Do NOT call checkAuth() here: the library has no in-flight dedup, so a
 * second concurrent call re-processes the same code, fails validation
 * ("authCallback incorrect nonce, did you call the checkAuth() method
 * multiple times?"), and its resetAuthorizationData() wipes the session the
 * first call just established — producing an infinite Authentik redirect
 * loop. This component only surfaces a genuine failure (and stays on this
 * unguarded route so the auto-login guard can't re-trigger the loop).
 */
@Component({
  selector: 'app-auth-callback',
  imports: [TranslateModule],
  template: `
    @if (error()) {
    <p>{{ 'layout.signInFailed' | translate : { error: error() } }}</p>
    } @else {
    <p>{{ 'layout.signingIn' | translate }}</p>
    }
  `,
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private readonly publicEventsService = inject(PublicEventsService)
  private readonly shared = inject(SharedService)
  private subscription?: Subscription

  protected readonly error = signal<string | null>(null)

  ngOnInit(): void {
    // This route sits outside the layout shell (which normally applies the
    // saved language), so apply it here too or the pipe would show raw keys.
    let lang = 'ar'
    try {
      lang = JSON.parse(localStorage.getItem('language') || '"ar"') || 'ar'
    } catch {
      lang = 'ar'
    }
    this.shared.setLangSettings(lang)
    this.subscription = this.publicEventsService
      .registerForEvents()
      .pipe(filter(event => event.type === EventTypes.NewAuthenticationResult))
      .subscribe(event => {
        const result = event.value as { isAuthenticated: boolean; isRenewProcess: boolean; validationResult: ValidationResult } | undefined
        if (result && !result.isAuthenticated && !result.isRenewProcess) {
          this.error.set(result.validationResult || 'not authenticated')
        }
      })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }
}
